// ============================================================================
// GST ENGAGEMENT SETUP MODAL — G-1 slice 1
// ============================================================================
// Configures GST for a client and financial year.
//
// TWO THINGS HERE ARE NOT ARBITRARY:
//
// 1. The engagement TYPE is a required user choice, not a module constant.
//    GST owns no engagement type — the pattern FSA follows. A module-owned
//    type would break for any tenant who had already created their own "GST
//    Filing" type: CreateOrGetAsync matches on (client, type, year), so the
//    client would silently acquire two GST engagements for one year.
//
// 2. GSTINs are PICKED from the client master, never typed. Adding a GSTIN
//    stays a client-master action; GST extends those rows rather than
//    restating them, so the number has exactly one home and cannot drift.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstEngagementService } from 'api/services/gstEngagementService';
import { clientService } from 'api/services/clientService';
import { engagementTypeService } from 'api/services/engagementTypeService';
import { employeeService } from 'api/services/employeeService';
import type {
  AvailableGstRegistration,
  CreateGstEngagementRegistrationRequest,
} from 'api/types/gstEngagement.types';
import ClientRegistrationsModal from 'components/modals/modules/client/ClientRegistrationsModal';
import { getModuleActions } from 'config/rolePermissions';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Present when hosted in a tab — enables minimise. */
  tabId?: string;
  moduleName?: string;
  /** Distinguishes simultaneously-parked Add sessions. */
  createInstanceId?: string;
  /** Lets the screen minimise this modal programmatically. */
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create', instanceId?: string) => void;
}

interface Option {
  value: string;
  label: string;
}

interface FormData {
  clientId: string;
  engagementTypeId: string;
  managerId: string;
  financialYear: string;
  filingNotes: string;
}

/**
 * QRMP files GSTR-1 quarterly but still files IFF and PMT-06 monthly, which is
 * why frequency drives the default return set rather than being cosmetic.
 */
const RETURN_TYPE_PRESETS: Record<string, string[]> = {
  MONTHLY: ['GSTR1', 'GSTR3B'],
  QRMP: ['GSTR1', 'IFF', 'PMT06', 'GSTR3B'],
};

const FREQUENCY_OPTIONS: Option[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QRMP', label: 'QRMP (quarterly)' },
];

const HSN_DIGIT_OPTIONS: Option[] = [
  { value: '4', label: '4 digits' },
  { value: '6', label: '6 digits' },
  { value: '8', label: '8 digits' },
];

/** India's financial year starts in April, so before April the current FY is
 *  still the previous calendar year. */
const currentFinancialYear = (): number => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

const fyLabel = (y: number) => `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`;

const INITIAL: FormData = {
  clientId: '',
  engagementTypeId: '',
  managerId: '',
  financialYear: String(currentFinancialYear()),
  filingNotes: '',
};

const GstEngagementModal: React.FC<Props> = ({
  isOpen, onClose, onSaved,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<FormData>(INITIAL);
  const initialSnapshot = useRef('');
  // Reading these through refs keeps handleMinimize stable while still
  // capturing the CURRENT values at the moment it fires.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  // Set only by an explicit restore, so a fresh Add after parking one does
  // not inherit the parked session's data.
  const isRestoreRef = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  // Engagement-type master is small — preloaded, unlike clients/employees.
  const [engagementTypeOptions, setEngagementTypeOptions] = useState<Option[]>([]);

  const [available, setAvailable] = useState<AvailableGstRegistration[]>([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [scoped, setScoped] = useState<Record<string, CreateGstEngagementRegistrationRequest>>({});
  const scopedRef = useRef(scoped);
  scopedRef.current = scoped;

  // Labels of clients seen in the async select, so the registrations
  // drill-down can name the client it opens for.
  const [clientLabels, setClientLabels] = useState<Record<string, string>>({});

  // Adding a GSTIN is a CLIENT-MASTER action (see note 2 in the header) — this
  // opens the very same drill-down the Client master opens, governed by the
  // same `client-registration` screen code. GST gains no write path of its own.
  const [registrationsOpen, setRegistrationsOpen] = useState(false);
  const registrationActions = useMemo(() => getModuleActions('client-registration'), []);
  const canAddRegistration = registrationActions.includes('add');

  const yearOptions = useMemo<Option[]>(() => {
    const base = currentFinancialYear();
    const out: Option[] = [];
    for (let y = base + 1; y > base - 5; y -= 1) out.push({ value: String(y), label: fyLabel(y) });
    return out;
  }, []);

  // Async loaders — a firm's client roster and employee list both outgrow a
  // preloaded dropdown, so both search server-side (the FSA modal's pattern).
  const loadClientOptions = useCallback(async (search: string) => {
    const resp = await clientService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined });
    const options = resp.data.map((c: any) => ({
      value: String(c.id),
      // Returns carry the legal name, so lead with it where one exists.
      label: c.legalName?.trim() ? `${c.legalName} (${c.name})` : c.name,
    }));
    // The select is async, so onChange hands back an id and nothing else.
    // Keep the labels seen so far so the registrations drill-down can name the
    // client in its header rather than opening anonymously.
    setClientLabels((prev) => {
      const next = { ...prev };
      for (const o of options) next[o.value] = o.label;
      return next;
    });
    return options;
  }, []);

  const loadManagerOptions = useCallback(async (search: string) => {
    const resp = await employeeService.getAll({
      page: 1, pageSize: 50, searchTerm: search || undefined, isManager: true,
    } as any);
    return resp.data.map((e: any) => ({
      value: String(e.id),
      label: e.code ? `${e.code} - ${e.name}` : e.name,
    }));
  }, []);

  // ---- reset on open ----------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    // A restore re-opens with the parked session's data already applied by
    // onRestore, so skip the wipe — but still refresh the engagement-type
    // lookup below, since one may have been created while this was parked.
    if (!isRestoreRef.current) {
      const next = { ...INITIAL };
      setFormData(next);
      initialSnapshot.current = JSON.stringify(next);
      setAvailable([]);
      setScoped({});
    }
    isRestoreRef.current = false;
    setFieldErrors({});

    engagementTypeService
      .getAll({ page: 1, pageSize: 100 })
      .then((r) => {
        setEngagementTypeOptions(
          r.data.map((t: any) => ({ value: String(t.id), label: `${t.code} · ${t.name}` })),
        );
      })
      .catch(() => {
        notificationService.toast.error('Could not load engagement types.');
      });
  }, [isOpen]);

  // ---- registrations for the chosen client ------------------------------
  useEffect(() => {
    if (!isOpen || !formData.clientId) {
      setAvailable([]);
      setScoped({});
      return;
    }
    let cancelled = false;
    setLoadingRegistrations(true);
    gstEngagementService
      .getAvailableRegistrations(formData.clientId)
      .then((regs) => {
        if (cancelled) return;
        setAvailable(regs);
        setScoped({});
      })
      .catch(() => {
        if (cancelled) return;
        setAvailable([]);
        notificationService.toast.error('Could not load GST registrations for this client.');
      })
      .finally(() => {
        if (!cancelled) setLoadingRegistrations(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, formData.clientId]);

  /**
   * Re-read the picker after the drill-down added a GSTIN. Deliberately does
   * NOT clear `scoped`: the person may already have ticked and configured
   * other registrations before noticing one was missing, and discarding that
   * work is the whole reason this shortcut exists.
   */
  const reloadAvailableRegistrations = useCallback(async () => {
    if (!formData.clientId) return;
    setLoadingRegistrations(true);
    try {
      setAvailable(await gstEngagementService.getAvailableRegistrations(formData.clientId));
    } catch {
      notificationService.toast.error('Could not reload GST registrations for this client.');
    } finally {
      setLoadingRegistrations(false);
    }
  }, [formData.clientId]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot.current) return false;
    if (Object.keys(scoped).length > 0) return true;
    return JSON.stringify(formData) !== initialSnapshot.current;
  }, [formData, scoped]);

  const setField = useCallback(<K extends keyof FormData>(k: K, v: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [k]: v }));
    setFieldErrors((prev) => {
      if (!prev[k as string]) return prev;
      const next = { ...prev };
      delete next[k as string];
      return next;
    });
  }, []);

  const toggleRegistration = useCallback((reg: AvailableGstRegistration) => {
    setFieldErrors((prev) => {
      if (!prev.registrations) return prev;
      const next = { ...prev };
      delete next.registrations;
      return next;
    });
    setScoped((prev) => {
      const next = { ...prev };
      if (next[reg.clientRegistrationId]) {
        delete next[reg.clientRegistrationId];
      } else {
        next[reg.clientRegistrationId] = {
          clientRegistrationId: reg.clientRegistrationId,
          tradeName: reg.tradeName ?? null,
          registrationType: reg.registrationType ?? 'REGULAR',
          filingFrequency: 'MONTHLY',
          requiredHsnDigits: 6,
          returnTypes: RETURN_TYPE_PRESETS.MONTHLY,
        };
      }
      return next;
    });
  }, []);

  const updateScoped = useCallback(
    (id: string, patch: Partial<CreateGstEngagementRegistrationRequest>) => {
      setScoped((prev) => {
        const row = prev[id];
        if (!row) return prev;
        const merged = { ...row, ...patch };
        // Changing frequency re-presets the return types, because QRMP files a
        // different set.
        if (patch.filingFrequency) {
          merged.returnTypes = RETURN_TYPE_PRESETS[patch.filingFrequency] ?? row.returnTypes;
        }
        return { ...prev, [id]: merged };
      });
    },
    [],
  );

  // April..March of the chosen year. '' = from the start of the year (the
  // default); a later month for a GSTIN registered or taken over mid-year.
  const monthOptions: Option[] = useMemo(() => {
    const y = Number(formData.financialYear);
    if (!y) return [{ value: '', label: 'Start of the year' }];
    const opts: Option[] = [{ value: '', label: `Start of the year (Apr ${y})` }];
    for (let i = 1; i < 12; i++) {
      const d = new Date(y, 3 + i, 1);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      opts.push({ value: iso, label: d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) });
    }
    return opts;
  }, [formData.financialYear]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  /**
   * Park this session in the tab's minimised strip.
   *
   * `scoped` is captured alongside `formData` because it holds half the work —
   * which GSTINs are in scope and how each is configured. Restoring the header
   * fields without it would look like the form came back intact while silently
   * dropping the registration setup.
   */
  const handleMinimize = useCallback(() => {
    if (!tabId) return;

    const instanceId = createInstanceIdRef.current || '1';
    const savedFormData = { ...formDataRef.current };
    const savedScoped = { ...scopedRef.current };
    const savedSnapshot = initialSnapshot.current;
    const currentIsDirty = savedSnapshot
      ? Object.keys(savedScoped).length > 0 || JSON.stringify(savedFormData) !== savedSnapshot
      : false;

    minimize({
      id: `${tabId}-form-modal-create-${instanceId}`,
      title: `Configure GST (${instanceId})`,
      moduleName: moduleName ?? 'GST Engagements',
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        setScoped(savedScoped);
        initialSnapshot.current = savedSnapshot;
        onRestoreModal?.('create', instanceId);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  // Exposed so the screen can park an open modal before opening a new Add.
  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.clientId) errors.clientId = 'Client is required';
    if (!formData.engagementTypeId) errors.engagementTypeId = 'Engagement type is required';
    if (!formData.managerId) errors.managerId = 'Manager is required';
    if (!formData.financialYear) errors.financialYear = 'Financial year is required';
    // A GST engagement with no GSTIN in scope has nothing to file — the
    // period generator downstream would produce zero periods.
    if (Object.keys(scoped).length === 0) {
      errors.registrations = 'Select at least one GSTIN to bring into scope';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setSaveConfirmOpen(true);
  }, [formData, scoped]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      await gstEngagementService.create({
        clientId: formData.clientId,
        engagementTypeId: formData.engagementTypeId,
        financialYear: Number(formData.financialYear),
        managerId: formData.managerId,
        filingNotes: formData.filingNotes || null,
        dataSourceMode: 'CLIENT_SUPPLIED',
        registrations: Object.values(scoped),
      });
      setSaving(false);
      notificationService.toast.success('GST configured.');
      onSaved();
    } catch (err: any) {
      setSaving(false);
      // The backend distinguishes "this engagement already has GST" (409) from
      // validation failures, so surface its message rather than a generic one.
      const data = err?.response?.data;
      const msg = data?.message
        ?? (Array.isArray(data?.errors) && data.errors.length ? data.errors.join('; ') : null)
        ?? err?.message
        ?? 'Could not configure GST for this client.';
      notificationService.toast.error(msg);
    }
  }, [formData, scoped, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-lg">
        {saving && (
          <div
            style={{
              position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Configuring GST…
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Configure GST</h2>
          {/* .modal-header is justify-content: space-between, so the buttons
              must share ONE flex child. Left as siblings of the title they get
              spread apart and minimise lands mid-header. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                disabled={saving}
                title="Minimize to bar"
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving} title="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="clientId">
              <SyutSelect
                label={<>Client <span className="text-danger">*</span></>}
                value={formData.clientId}
                onChange={(v) => setField('clientId', (v as string) || '')}
                isAsync
                loadOptions={loadClientOptions}
                defaultOptions
                cacheOptions={false}
                error={fieldErrors.clientId}
                placeholder="Search client…"
              />
            </div>
            <div className="form-group" data-field="financialYear">
              <SyutSelect
                label={<>Financial Year <span className="text-danger">*</span></>}
                options={yearOptions}
                value={formData.financialYear}
                onChange={(v) => setField('financialYear', (v as string) || '')}
                isClearable={false}
                error={fieldErrors.financialYear}
              />
            </div>
            <div className="form-group" data-field="engagementTypeId">
              <SyutSelect
                label={<>Engagement Type <span className="text-danger">*</span></>}
                options={engagementTypeOptions}
                value={formData.engagementTypeId}
                onChange={(v) => setField('engagementTypeId', (v as string) || '')}
                error={fieldErrors.engagementTypeId}
                placeholder="Select engagement type"
              />
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                GST attaches to whichever type you use for this work — it does not own one.
              </div>
            </div>
            <div className="form-group" data-field="managerId">
              <SyutSelect
                label={<>Manager <span className="text-danger">*</span></>}
                value={formData.managerId}
                onChange={(v) => setField('managerId', (v as string) || '')}
                isAsync
                loadOptions={loadManagerOptions}
                defaultOptions
                cacheOptions={false}
                error={fieldErrors.managerId}
                placeholder="Search manager…"
              />
            </div>
          </div>

          <div className="form-group" data-field="registrations">
            <label className="form-label">
              GSTIN registrations in scope <span className="text-danger">*</span>
            </label>
            <div
              style={{
                fontSize: '0.72rem',
                color: 'var(--text-secondary)',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>Listed from the client master — GST never invents a registration.</span>
              {formData.clientId && canAddRegistration && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                  disabled={saving}
                  onClick={() => setRegistrationsOpen(true)}
                >
                  <IconSystem.add size={12} style={{ marginRight: '0.25rem' }} />
                  Add GSTIN
                </button>
              )}
            </div>

            {!formData.clientId && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Select a client to see their registrations.
              </div>
            )}

            {formData.clientId && loadingRegistrations && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Loading registrations…
              </div>
            )}

            {formData.clientId && !loadingRegistrations && available.length === 0 && (
              <div style={{ fontSize: '0.8125rem', color: '#dc2626' }}>
                {canAddRegistration
                  ? 'This client has no GSTIN on their client record. Use Add GSTIN above — it opens the client’s registrations without losing what you have filled in here.'
                  : 'This client has no GSTIN on their client record. Ask someone with client registration access to record it, then return here.'}
              </div>
            )}

            {available.map((reg) => {
              const sel = scoped[reg.clientRegistrationId];
              return (
                <div
                  key={reg.clientRegistrationId}
                  style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: 6,
                    padding: '0.625rem',
                    marginBottom: '0.5rem',
                    background: sel ? 'var(--bg-secondary)' : 'transparent',
                  }}
                >
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(sel)}
                      disabled={saving}
                      onChange={() => toggleRegistration(reg)}
                    />
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {reg.gstin}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      state {reg.stateCode}
                      {reg.jurisdiction ? ` · ${reg.jurisdiction}` : ''}
                    </span>
                  </label>

                  {sel && (
                    <div className="form-grid-3" style={{ marginTop: 10, paddingLeft: 28 }}>
                      <div className="form-group">
                        <SyutSelect
                          label="Filing frequency"
                          options={FREQUENCY_OPTIONS}
                          value={sel.filingFrequency}
                          isClearable={false}
                          size="sm"
                          onChange={(v) =>
                            updateScoped(reg.clientRegistrationId, {
                              filingFrequency: ((v as string) || 'MONTHLY') as 'MONTHLY' | 'QRMP',
                            })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <SyutSelect
                          label="HSN digits"
                          options={HSN_DIGIT_OPTIONS}
                          value={String(sel.requiredHsnDigits)}
                          isClearable={false}
                          size="sm"
                          onChange={(v) =>
                            updateScoped(reg.clientRegistrationId, {
                              requiredHsnDigits: Number((v as string) || '6'),
                            })
                          }
                        />
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                          Follows aggregate turnover for the year.
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Returns</label>
                        <div style={{ paddingTop: 6, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {sel.returnTypes.join(' · ')}
                        </div>
                      </div>
                      <div className="form-group">
                        <SyutSelect
                          label="Firm files from"
                          options={monthOptions}
                          value={sel.scopeFrom ? String(sel.scopeFrom).slice(0, 10) : ''}
                          isClearable={false}
                          size="sm"
                          onChange={(v) =>
                            updateScoped(reg.clientRegistrationId, { scopeFrom: (v as string) || null })
                          }
                        />
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                          A GSTIN registered, or taken over, mid-year. No periods are created before it.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {fieldErrors.registrations && (
              <div className="form-error-message">{fieldErrors.registrations}</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Filing notes</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={formData.filingNotes}
              disabled={saving}
              onChange={(e) => setField('filingNotes', e.target.value)}
              placeholder="Client contact preferences, filing quirks — never credentials"
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>Configure GST</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`Configure GST for ${Object.keys(scoped).length} registration(s) for ${fyLabel(Number(formData.financialYear))}?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />

      {/*
        The client master's own registrations drill-down, opened in place so a
        missing GSTIN can be recorded without abandoning this form. It writes
        through ClientRegistrationsController like everywhere else — GST gains
        no second write path for a registration number.
      */}
      <ClientRegistrationsModal
        isOpen={registrationsOpen}
        clientId={formData.clientId || null}
        clientName={clientLabels[formData.clientId] ?? null}
        allowedActions={registrationActions}
        defaultRegistrationTypeCode="GSTIN"
        onClose={() => setRegistrationsOpen(false)}
        onChanged={reloadAvailableRegistrations}
      />
    </>
  );
};

export default GstEngagementModal;
